<template>
  <StartQuoteStepper
    :is-mobile="isMobile"
    :max-width="isMobile ? undefined : '672px'"
    :title="title"
    :post-accepted-action-text="postAcceptedActionText"
    :tabs="tabs"
    :open="open"
    :display-close="false"
    :has-started="isCreditOnly || hasStartedApplication"
    :has-car-search="isCreditOnly || hasCarSearch"
    :has-submission="isCreditOnly || hasSubmission"
    :has-accepted-quote="isCreditOnly || Boolean(acceptedQuoteId)"
    :has-credit-check="isCreditOnly || Boolean(applicationCreditCheckId)"
    :has-submitted-credit-check="hasSubmittedCreditCheck"
    :has-completed-credit-check="creditCheckComplete"
    :has-sent-hr-email="hrRequestSent"
    :loading-quote="quotesLoading[acceptedQuoteId]"
    :accepted-quote="quotes[acceptedQuoteId]"
    :block-create-quote="!canCreateQuote"
    @open:quote="openQuote"
    @open:quote-summary="openQuoteSummary"
    @continue:application="continueApplication"
    @set:open="open = $event"
  />
</template>

<script>
import { getEnv } from 'ecx.saigon.ui.common';
import { mapGetters, mapState } from 'vuex';
import _get from 'lodash/get';
import getFurthestQuoteRoute from '@/utils/get-furthest-quote-route';
import resolveLocalRoute from '@/utils/resolve-local-route';
import { profileCompleteGuard } from '@/utils/profile-complete-guard';
import NewQuoteLeaseSelector from '@/components/organism/new-quote-lease-selector';

export default {
  name: 'StartQuoteStepperWidget',
  data() {
    return {
      open: true
    };
  },
  computed: {
    ...mapState({
      application: (state) => state.application.application,
      quotes: (state) => state.dashboard.quotes,
      quotesLoading: (state) => state.dashboard.quotesLoading
    }),
    isCreditOnly() {
      return this.userIsAccessLevel([
        this.$Constants.USER_ACCESS_LEVEL.CREDIT_ONLY
      ]);
    },
    isMobile() {
      return this.$q.screen.xs;
    },
    hasCarSearch() {
      return Boolean(
        _get(this.application, 'metaData.customerOptions.variant')
      );
    },
    hasSubmission() {
      return Boolean(this.hasAnyQuote);
    },
    hasSubmittedCreditCheck() {
      return this.creditCheckStatus === 'refer';
    },
    ...mapGetters(['canCreateQuote', 'allowEndOfLifeQuote']),
    ...mapGetters('application', [
      'hasStartedApplication',
      'hasAnyQuote',
      'acceptedQuoteId',
      'applicationCreditCheckId',
      'creditCheckComplete',
      'creditCheckStatus',
      'hrRequestSent'
    ]),
    ...mapGetters('user', ['userIsAccessLevel']),
    ...mapGetters('lease', ['nearExpirationRegos']),
    title() {
      if (!this.hasStartedApplication) {
        return 'Find out how much you could save';
      }

      if (!this.hasSubmission) {
        return 'Want to pick up where you left off? The next step is selecting your car';
      }

      return undefined;
    },
    postAcceptedActionText() {
      // If the hr request has been sent
      if (this.hrRequestSent) {
        // eslint-disable-next-line
        return 'We are waiting on your employer\'s approval.';
      }
      // If the user has completed the credit check but not yet sent the hr request
      if (this.creditCheckComplete) {
        // eslint-disable-next-line
        return 'Before we order your car, we need the \'OK\' from your employer';
      }
      // If the user started but not finished the credit check
      if (this.applicationCreditCheckId) {
        return 'Only a few questions left to complete your finance application';
      }
      // If the user has not started a credit check
      // eslint-disable-next-line
      return 'Now it\'s time to apply for your vehicle finance';
    },
    tabs() {
      return [
        {
          active: !this.hasStartedApplication,
          disable: false,
          label: 'Car Finance',
          icon: 'icon-nav-step-1',
          checked: this.hasStartedApplication
        },
        {
          active: this.hasStartedApplication && !this.hasSubmission,
          disable: !this.hasStartedApplication,
          label: 'Car Selection',
          icon: 'icon-nav-step-2',
          iconSize: '24px',
          iconStyle: 'width: 44px;',
          checked: this.hasSubmission
        },
        {
          active: false,
          disable: true,
          label: 'Finance Approval',
          icon: 'icon-nav-step-3',
          checked: false
        },
        {
          active: false,
          disable: true,
          label: 'Employer Approval',
          icon: 'icon-nav-step-4',
          checked: false
        }
      ];
    }
  },
  methods: {
    async openQuote() {
      if (!profileCompleteGuard(this)) return;

      const env = await getEnv();

      if (this.allowEndOfLifeQuote) {
        this.$q
          .dialog({
            component: NewQuoteLeaseSelector,
            nearExpirationRegos: this.nearExpirationRegos
          })
          .onOk(async ({ leaseId }) => {
            window.location = resolveLocalRoute(
              `${env.VUE_APP_QUOTE_ROUTE}${
                leaseId ? `/calculator?leaseId=${leaseId}` : ''
              }`
            );
          });
      } else {
        window.location = resolveLocalRoute(env.VUE_APP_QUOTE_ROUTE);
      }
    },
    async openQuoteSummary() {
      const env = await getEnv();
      const quote = this.quotes[this.acceptedQuoteId];
      window.location = resolveLocalRoute(
        `${env.VUE_APP_QUOTE_ROUTE}/${quote.quoteId}/summary`
      );
    },
    async continueApplication() {
      const route = await getFurthestQuoteRoute(
        this.acceptedQuoteId,
        this.quotes[this.acceptedQuoteId].quoteId,
        this.application
      );
      window.location = resolveLocalRoute(route);
    }
  },
  components: {
    StartQuoteStepper: () =>
      import('../../components/organism/start-quote-stepper')
  }
};
</script>
